import { useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { SetupIntent, StripeError } from '@stripe/stripe-js';
import { useSnackbar } from '@lambdacurry/component-library';
import { StandardAddress } from 'lib/home/generated/types/api';
import { stripeBillingDetailsFromStandardAddress } from 'lib/home/util/addresses';
import { useSetDefaultMutation } from '../../components/PaymentMethod/PaymentMethod.hooks';
import { siteURL } from '../../util/constants';

export interface UseStripeConfirmSetupIntentConfig {
  redirectPath: string;
}

export interface UseStripeConfirmSetupIntentValues extends Omit<StandardAddress, 'id'> {}

export interface UseStripeConfirmSetupIntentOptions {
  onSuccess: (setupIntent: SetupIntent) => void;
  onError: (error: StripeError | Error) => void;
}

export const useStripeConfirmSetupIntent = ({ redirectPath }: UseStripeConfirmSetupIntentConfig) => {
  const stripe = useStripe();
  const elements = useElements();
  const setDefaultMutation = useSetDefaultMutation();
  const { addSnackbar } = useSnackbar();
  const [confirmedSetupIntent, setConfirmedSetupIntent] = useState<SetupIntent | null>(null);

  const confirm = (values: UseStripeConfirmSetupIntentValues, options?: UseStripeConfirmSetupIntentOptions) => {
    const { is_default, ...address } = values;
    const { onSuccess, onError } = options;

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    // If we already have a confirmed setupIntent, we shouldn't attempt to confirm again.
    if (confirmedSetupIntent) return onSuccess(confirmedSetupIntent);

    const stripeBillingDetails = stripeBillingDetailsFromStandardAddress(address);

    const handleSuccess = (setupIntent: SetupIntent) => {
      setConfirmedSetupIntent(setupIntent);
      if (onSuccess) onSuccess(setupIntent);
    };

    const handleError = (error: StripeError | Error) => {
      if (onError) onError(error);
      addSnackbar(error.message, { variant: 'error' });
    };

    // NOTE: We cannot use async await here, as Formik automatically sets the `isSubmitting` state to `false`
    // as soon as the first `await` resolves. Since we also need to conditionally call the
    // `setDefaultUserPaymentMethodMutation` upon success, we need to perform that with the `then` method.
    // SEE: https://github.com/jaredpalmer/formik/issues/1730
    stripe
      .confirmSetup({
        //`Elements` instance that was used to create the Payment Element
        elements,
        confirmParams: {
          return_url: siteURL(redirectPath),

          // We need to add the billing details manually because we are disabling
          // the billing address fields on the `PaymentElement`
          payment_method_data: { billing_details: stripeBillingDetails }
        },
        redirect: 'if_required'
      })
      .then(({ setupIntent, error }) => {
        // This point will only be reached if there is an immediate error when
        // confirming the payment. Show error to your customer (e.g., payment
        // details incomplete)
        if (error) return handleError(error);

        if (!is_default) return handleSuccess(setupIntent);

        // Set the user's default payment method.
        setDefaultMutation.mutate(
          { paymentMethodId: setupIntent.payment_method },
          { onSuccess: () => handleSuccess(setupIntent) }
        );
      })
      .catch(error => handleError(error));

    // Your customer will be redirected to your `return_url`. For some payment
    // methods like iDEAL, your customer will be redirected to an intermediate
    // site first to authorize the payment, then redirected to the `return_url`.
  };

  return { confirm, confirmedSetupIntent };
};
